import { type TableHighlightsLayoutAdapter } from '@databases/highlights';

const PINNED_LEADING_BOUNDARY_ATTRIBUTE = 'data-dbs-pinned-leading-freeze-boundary';
const PINNED_LEADING_BOUNDARY_SELECTOR = `[${PINNED_LEADING_BOUNDARY_ATTRIBUTE}="true"][data-col-index]`;

function containsPinnedBoundary(nodes: NodeList): boolean {
  return Array.from(nodes).some(
    (node) =>
      node instanceof Element &&
      (node.matches(PINNED_LEADING_BOUNDARY_SELECTOR) ||
        node.querySelector(PINNED_LEADING_BOUNDARY_SELECTOR) !== null),
  );
}

function containsTableHeader(nodes: NodeList): boolean {
  return Array.from(nodes).some(
    (node) =>
      node instanceof Element &&
      (node.matches('thead') || node.querySelector('thead') !== null),
  );
}

/**
 * Adapts Table V1's DOM boundary marker to the layout-neutral highlights port.
 * The attribute query remains isolated here so a future table renderer can
 * provide layout state without reproducing Table V1's DOM structure.
 */
export function createTableHighlightsLayoutAdapter(
  getRoot: () => HTMLElement | null,
): TableHighlightsLayoutAdapter {
  function getPinnedLeadingColumnCount() {
    const boundary = getRoot()?.querySelector(PINNED_LEADING_BOUNDARY_SELECTOR);

    if (!boundary) {
      return 0;
    }

    const boundaryColumnIndex = Number.parseInt(
      boundary.getAttribute('data-col-index') ?? '',
      10,
    );

    return Number.isFinite(boundaryColumnIndex)
      ? Math.max(0, boundaryColumnIndex + 1)
      : 0;
  }

  return {
    getPinnedLeadingColumnCount,

    subscribeToLayoutChanges(listener) {
      const root = getRoot();

      if (!root) {
        return () => {};
      }

      const layoutRoot: HTMLElement = root;
      let previousCount = getPinnedLeadingColumnCount();
      const observer = new MutationObserver((mutations) => {
        const headerTreeChanged = mutations.some(
          (mutation) =>
            mutation.type === 'childList' &&
            (containsTableHeader(mutation.addedNodes) ||
              containsTableHeader(mutation.removedNodes)),
        );

        if (headerTreeChanged) {
          observeCurrentHeader();
        }

        const boundaryChanged = mutations.some(
          (mutation) =>
            (mutation.type === 'childList' &&
              (containsPinnedBoundary(mutation.addedNodes) ||
                containsPinnedBoundary(mutation.removedNodes))) ||
            (mutation.type === 'attributes' &&
              (mutation.attributeName === PINNED_LEADING_BOUNDARY_ATTRIBUTE ||
                mutation.attributeName === 'data-col-index')),
        );

        if (!boundaryChanged && !headerTreeChanged) {
          return;
        }

        const nextCount = getPinnedLeadingColumnCount();

        if (nextCount === previousCount) {
          return;
        }

        previousCount = nextCount;
        listener();
      });

      function observeCurrentHeader() {
        observer.disconnect();
        const observedHeader = layoutRoot.querySelector('thead');

        if (!observedHeader) {
          observer.observe(layoutRoot, {
            attributes: true,
            childList: true,
            subtree: true,
            attributeFilter: [PINNED_LEADING_BOUNDARY_ATTRIBUTE, 'data-col-index'],
          });
          return;
        }

        observer.observe(observedHeader, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: [PINNED_LEADING_BOUNDARY_ATTRIBUTE, 'data-col-index'],
        });

        let ancestor = observedHeader.parentElement;

        while (ancestor) {
          observer.observe(ancestor, { childList: true });

          if (ancestor === layoutRoot) {
            break;
          }

          ancestor = ancestor.parentElement;
        }
      }

      observeCurrentHeader();

      return () => observer.disconnect();
    },
  };
}
